import React, { useState } from "react";
import { Link, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Divider,
  Typography,
  Paper,
  Alert,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";

import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

import { CssVarsProvider } from "@mui/joy/styles";
import CssBaseline from "@mui/joy/CssBaseline";

import Snackbar from "@mui/joy/Snackbar";

import dayjs from "dayjs";
import { getCadence } from "lib/cadence";
import {
  useDepositMutation,
  useGetActiveStatusQuery,
  useWithdrawMutation,
  useGetStatusQuery,
} from "components/Slices/usersApiSlice";
import { useGetStripeQuery } from "components/Slices/accountsApiSlice";
import PayoutOrder from "../PayoutOrder";
import CircleButton from "../CircleButton";

import { Elements } from "@stripe/react-stripe-js";

import DepositModal from "./DepositModal";
import WithdrawModal from "./WithdrawModal";
import PaymentBreakdown from "./PaymentBreakdown";

import { stripePromise } from "index";
interface Group {
  id: string;
  title: string;
  description: string;
  monthlyContribution: number;
  nextPayout: string;
  payoutAmount: number;
  currency: string;
  intervalUnit?: "day" | "week" | "month";
  intervalCount?: number;
}

interface WithdrawType {
  message: string;
  transferId: string;
  amount: number;
  currency: string;
}

interface FeeBreakdown {
  baseAmount: number;
  stripeFee: number;
  platformFee: number;
  totalFees: number;
  totalAmount: number;
  paymentMethodType: string;
}

interface DepositResponse {
  clientSecret: string;
  customerSession: string;
  paymentIntentId: string;
  baseAmount: number;
  currency: string;
  feesByMethod: Record<string, FeeBreakdown>;
  selectedMethod: string;
}

function ActivePage({ group }: { group: Group }) {
  const [deposit] = useDepositMutation();
  const [withdraw] = useWithdrawMutation();

  const [loading, setLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState<string>();
  const [customerSecret, setCustomerSecret] = useState<string>();
  const [openDeposit, setOpenDeposit] = useState(false);
  const [openWithdraw, setOpenWithdraw] = useState(false);
  const [depositData, setDepositData] = useState<DepositResponse | null>(null);

  const [withdrawResponse, setWithdrawResponse] = useState<WithdrawType | null>(
    null,
  );
  const [confirmWithdraw, setConfirmWithdraw] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  // Machine-readable `code` from the backend, when it sends one. Drives the
  // recovery CTA below so we don't have to sniff the human-readable message.
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const options = {
    clientSecret: clientSecret,
    customerSessionClientSecret: customerSecret,
  };

  const handleDeposit = async () => {
    setLoading(true);
    setOpenDeposit(true);
    try {
      const groupId = group.id;
      const response = await deposit({
        groupId,
      }).unwrap();
      setCustomerSecret(response.customerSession);
      setClientSecret(response.clientSecret);
      setDepositData(response);
      setLoading(false);
    } catch (err) {
      setError(true);
      console.error("Deposit error:", err);
      setErrorMessage("An error occurred while processing the deposit.");
      setErrorCode(null);
      console.error({ err });
    }
  };

  const handleWithdrawClick = () => {
    setConfirmWithdraw(true);
  };

  const handleWithdrawConfirm = async () => {
    setConfirmWithdraw(false);
    setLoading(true);
    setOpenWithdraw(true);
    try {
      const res = await withdraw({
        groupId: group.id,
      }).unwrap();
      setWithdrawResponse(res);
    } catch (err: unknown) {
      console.error("Withdraw error:", err);
      setError(true);
      const data =
        typeof err === "object" && err !== null && "data" in err
          ? (err as any).data
          : null;
      if (typeof data?.message === "string") {
        setErrorMessage(data.message);
      } else {
        setErrorMessage("An error occurred while processing the withdrawal.");
      }
      setErrorCode(typeof data?.code === "string" ? data.code : null);
    }
    setLoading(false);
  };

  const { data: activeStatus, isLoading: isStatusLoading } =
    useGetActiveStatusQuery({ groupId: group.id });

  const { data: userStatus } = useGetStatusQuery();
  const { data: stripeAccount } = useGetStripeQuery();

  const isVerified = userStatus?.userStatus?.toLowerCase() === "active";
  const hasBankAccount =
    stripeAccount?.externalAccounts &&
    stripeAccount.externalAccounts.length > 0;

  const formatDeadline = (date: string | null) => {
    if (!date) return null;
    return new Date(date).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const verificationDeadline = formatDeadline(
    activeStatus?.verificationDeadline ?? null,
  );
  const bankDeadline = formatDeadline(activeStatus?.bankDeadline ?? null);
  const myPayoutDate = formatDeadline(activeStatus?.myPayoutDate ?? null);

  if (activeStatus === undefined) {
    console.error("Active status data is undefined.");
    return null;
  }

  const { isPayee, canPayout, status, allPaid } = activeStatus || {};

  // A failed withdrawal is the worst moment to hand someone a dead end — they are
  // owed money right now. When the backend tells us *why* it failed, route them
  // straight at the fix. The message fallback covers a backend deployed before
  // `code` existed, which still returns the same bank-settings copy.
  const payoutRecoveryCta =
    errorCode === "PAYOUT_ACCOUNT_REQUIRED"
      ? { to: "/onboarding", label: "Set up payouts →" }
      : errorCode === "PAYOUT_ACCOUNT_INCOMPLETE" ||
          (!errorCode && errorMessage.includes("check your bank settings"))
        ? { to: "/settings?tab=finances", label: "Check your settings →" }
        : null;

  return (
    <Paper
      component="form"
      sx={{
        p: { xs: "1rem", sm: "2rem" },
        m: { xs: "0.5rem", sm: "2rem" },
        borderRadius: "20px",
      }}
    >
      <Box sx={{ m: "1rem" }}>
        {/* Payout readiness warnings */}
        {(!isVerified || !hasBankAccount) && (
          <Stack spacing={1.5} sx={{ mb: 3 }}>
            {!isVerified && (
              <Alert
                severity="warning"
                action={
                  <RouterLink
                    to="/onboarding"
                    style={{
                      color: "inherit",
                      fontWeight: 700,
                      fontSize: "0.8rem",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Verify now →
                  </RouterLink>
                }
              >
                <Typography variant="body2" fontWeight={600}>
                  Identity verification required
                </Typography>
                <Typography variant="caption" display="block">
                  {verificationDeadline
                    ? `You must verify before ${verificationDeadline} to receive your payout${myPayoutDate ? ` on ${myPayoutDate}` : ""}.`
                    : `You must verify your identity before your payout turn${myPayoutDate ? ` on ${myPayoutDate}` : ""}.`}{" "}
                  You will be removed from this circle if not verified in time.
                </Typography>
              </Alert>
            )}
            {!hasBankAccount && (
              <Alert
                severity="warning"
                action={
                  <RouterLink
                    to="/settings?tab=finances"
                    style={{
                      color: "inherit",
                      fontWeight: 700,
                      fontSize: "0.8rem",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Add bank →
                  </RouterLink>
                }
              >
                <Typography variant="body2" fontWeight={600}>
                  Bank account required
                </Typography>
                <Typography variant="caption" display="block">
                  {bankDeadline
                    ? `Link a bank account before ${bankDeadline} to receive your payout${myPayoutDate ? ` on ${myPayoutDate}` : ""}.`
                    : `You need a linked bank account before your payout turn${myPayoutDate ? ` on ${myPayoutDate}` : ""}.`}{" "}
                  You will be removed from this circle if not added in time.
                </Typography>
              </Alert>
            )}
          </Stack>
        )}

        <Box>
          <Typography
            variant="h3"
            component="h1"
            sx={{ color: "text.primary" }}
          >
            {group.title}
          </Typography>
          {group.description && (
            <Typography
              variant="body1"
              sx={{ color: "text.secondary", mt: 1, maxWidth: 640 }}
            >
              {group.description}
            </Typography>
          )}
        </Box>

        {/* Key numbers. These replaced greyed-out disabled form inputs — the
            most important figures on the page were rendering in disabled-text
            grey and the description truncated in a single-line input. */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
            gap: 2,
            mt: 3,
            mb: 2,
          }}
        >
          <Box
            sx={{
              borderRadius: 3,
              p: 2.25,
              bgcolor: "rgba(46, 125, 50, 0.06)",
              border: "1px solid rgba(46, 125, 50, 0.18)",
            }}
          >
            <Typography variant="overline" sx={{ color: "text.secondary" }}>
              {isPayee ? "Your Payout" : "Amount Due"}
            </Typography>
            <Typography
              sx={{
                fontSize: "1.6rem",
                fontWeight: 700,
                color: "primary.dark",
                fontVariantNumeric: "lining-nums tabular-nums",
                lineHeight: 1.2,
              }}
            >
              $
              {(isPayee
                ? group.payoutAmount
                : group.monthlyContribution
              ).toFixed(2)}{" "}
              <Typography
                component="span"
                sx={{
                  fontSize: "0.95rem",
                  fontWeight: 600,
                  color: "text.secondary",
                }}
              >
                {group.currency}
              </Typography>
            </Typography>
          </Box>
          <Box
            sx={{
              borderRadius: 3,
              p: 2.25,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography variant="overline" sx={{ color: "text.secondary" }}>
              {isPayee ? "Payout Date" : "Due Date"}
            </Typography>
            <Typography
              sx={{ fontSize: "1.25rem", fontWeight: 650, lineHeight: 1.45 }}
            >
              {dayjs(group.nextPayout).format("MMM D, YYYY")}
            </Typography>
          </Box>
          <Box
            sx={{
              borderRadius: 3,
              p: 2.25,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography variant="overline" sx={{ color: "text.secondary" }}>
              Frequency
            </Typography>
            <Typography
              sx={{ fontSize: "1.25rem", fontWeight: 650, lineHeight: 1.45 }}
            >
              {getCadence(group.intervalUnit, group.intervalCount).adjective}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ mt: 4, mb: 4 }}>
          <Typography variant="h5" component="h2" sx={{ mb: 1 }}>
            Participants
          </Typography>
          <Divider sx={{ mb: 3 }} />
          <PayoutOrder />
        </Box>
        {/* Payment Breakdown - only show for non-payees */}
        {!isPayee && (
          <PaymentBreakdown
            monthlyContribution={group.monthlyContribution}
            currency={group.currency}
            intervalUnit={group.intervalUnit}
            intervalCount={group.intervalCount}
          />
        )}
        <CircleButton
          loading={isStatusLoading}
          isPayee={isPayee}
          allPaid={allPaid}
          canPayout={canPayout}
          status={status}
          onWithdraw={handleWithdrawClick}
          onDeposit={handleDeposit}
        />

        {clientSecret && customerSecret && depositData && (
          <Elements stripe={stripePromise} options={options}>
            <DepositModal
              circleId={group.id}
              open={openDeposit}
              handleClose={() => {
                setOpenDeposit(false);
                window.location.reload();
              }}
              depositData={depositData}
            />
          </Elements>
        )}
        <Dialog
          open={confirmWithdraw}
          onClose={() => setConfirmWithdraw(false)}
          PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
        >
          <DialogTitle>Confirm Withdrawal</DialogTitle>
          <DialogContent>
            <Typography>
              You are about to withdraw{" "}
              <strong>
                ${group.payoutAmount.toLocaleString()} {group.currency}
              </strong>{" "}
              to your bank account. This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmWithdraw(false)} color="inherit">
              Cancel
            </Button>
            <Button
              onClick={handleWithdrawConfirm}
              variant="contained"
              color="primary"
            >
              Confirm Withdrawal
            </Button>
          </DialogActions>
        </Dialog>

        {openWithdraw && withdrawResponse && (
          <WithdrawModal
            open={openWithdraw}
            onClose={() => {
              (setOpenWithdraw(false), window.location.reload());
            }}
            amount={withdrawResponse?.amount || 0}
            currency={withdrawResponse?.currency}
          />
        )}
      </Box>
      <CssVarsProvider>
        <CssBaseline />
        <Snackbar
          // Keep an actionable error on screen: 6s is not long enough to read it,
          // decide, and tap the link.
          autoHideDuration={payoutRecoveryCta ? null : 6000}
          color="danger"
          variant="soft"
          open={error}
          onClose={() => setError(false)}
          startDecorator={<ErrorOutlineIcon />}
        >
          <Box>
            {errorMessage}
            {payoutRecoveryCta && (
              <Link
                to={payoutRecoveryCta.to}
                style={{
                  marginLeft: "8px",
                  color: "inherit",
                  fontWeight: 600,
                }}
              >
                {payoutRecoveryCta.label}
              </Link>
            )}
          </Box>
        </Snackbar>
      </CssVarsProvider>
    </Paper>
  );
}

export default ActivePage;
